import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { initializeApp } from "firebase/app";
import AuthService from "../services/AuthService";
import { useUser } from "../contexts/UserContext";
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";

const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const firebaseConfig = JSON.parse(
  process.env.REACT_APP_FIREBASE_CONFIG || "{}",
);

let app;
let auth;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
} catch (error) {
  console.error("❌ Firebase initialization failed:", error);
}

const InputField = ({
  name,
  type,
  placeholder,
  value,
  onChange,
  error,
  ...props
}) => (
  <div className="mb-3">
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`form-control ${error ? "is-invalid" : ""}`}
      required
      {...props}
    />
    {error && <div className="invalid-feedback">{error}</div>}
  </div>
);

const countryCodes = [
  { code: "+91", name: "India" },
  { code: "+1", name: "USA" },
  { code: "+44", name: "UK" },
  { code: "+61", name: "Australia" },
  { code: "+971", name: "UAE" },
  { code: "+966", name: "Saudi Arabia" },
  { code: "+974", name: "Qatar" },
  { code: "+965", name: "Kuwait" },
  { code: "+973", name: "Bahrain" },
  { code: "+968", name: "Oman" },
  { code: "+977", name: "Nepal" },
  { code: "+65", name: "Singapore" },
  { code: "+60", name: "Malaysia" },
  { code: "+66", name: "Thailand" },
  { code: "+256", name: "Uganda" },
  { code: "+234", name: "Nigeria" },
  { code: "+251", name: "Ethiopia" },
  { code: "+233", name: "Ghana" },
  { code: "+880", name: "Bangladesh" },
  { code: "+95", name: "Myanmar" },
  { code: "+231", name: "Liberia" },
  { code: "+218", name: "Libya" },
  { code: "+229", name: "Benin" },
  { code: "+7", name: "Russia" },
  { code: "+967", name: "Yemen" },
];

const RegisterForm = () => {
  const navigate = useNavigate();
  const { setUser } = useUser();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    password: "",
    password_confirmation: "",
    countryCode: "+91",
    referral_code: "",
  });
  const [verificationCode, setVerificationCode] = useState("");
  const [message, setMessage] = useState({ text: "", type: "error" });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const recaptchaVerifierRef = useRef(null);
  const confirmationResultRef = useRef(null);

  useEffect(() => {
    if (!auth) return;

    recaptchaVerifierRef.current = new RecaptchaVerifier(
      auth,
      "recaptcha-container",
      {
        size: "invisible",
      },
    );

    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
      }
    };
  }, []);

  useEffect(() => {
    const referralCode = searchParams.get("ref");

    if (referralCode) {
      setForm((prev) => ({
        ...prev,
        referral_code: referralCode,
      }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Full name is required.";
    const fullPhone = form.countryCode + form.phone.replace(/^\+/, "");
    if (!/^\+[1-9]\d{10,14}$/.test(fullPhone)) {
      newErrors.phone = "Please enter a valid phone number.";
    }
    if (form.password.length < 6)
      newErrors.password = "Password must be at least 6 characters.";
    if (form.password !== form.password_confirmation)
      newErrors.password_confirmation = "Passwords do not match.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendVerification = async (e) => {
    e.preventDefault();
    if (!validateStep1()) return;
    setIsLoading(true);
    setMessage({ text: "", type: "error" });

    try {
      const fullPhone = form.countryCode + form.phone.replace(/^\+/, "");
      const verifier = recaptchaVerifierRef.current;

      confirmationResultRef.current = await signInWithPhoneNumber(
        auth,
        fullPhone,
        verifier,
      );

      setMessage({
        text: "A verification code has been sent to your phone!",
        type: "success",
      });
      setStep(2);
    } catch (error) {
      console.error("Phone sign-in error:", error);
      let msg =
        "Failed to send verification code. Please check the number and try again.";
      if (error.code === "auth/invalid-phone-number") {
        msg = "The phone number format is not valid.";
        setErrors((prev) => ({ ...prev, phone: msg }));
      } else if (error.code === "auth/too-many-requests") {
        msg = "Too many requests. Please try again later.";
      } else if (error.code === "auth/captcha-check-failed") {
        msg = "reCAPTCHA verification failed. Please try again.";
      }
      setMessage({ text: msg, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length !== 6) {
      setMessage({
        text: "Please enter the 6-digit verification code.",
        type: "error",
      });
      return;
    }

    setIsLoading(true);
    setMessage({ text: "", type: "error" });

    if (!confirmationResultRef.current) {
      setMessage({
        text: "Verification process expired. Please start over.",
        type: "error",
      });
      setStep(1);
      setIsLoading(false);
      return;
    }

    try {
      const credential =
        await confirmationResultRef.current.confirm(verificationCode);
      const user = credential.user;
      const idToken = await user.getIdToken();

      const backendUser = await AuthService.register({
        id_token: idToken,
        name: form.name,
        password: form.password,
        referral_code: form.referral_code,
      });

      setUser(backendUser);

      navigate("/premium/checkout", {
        state: { referral_code: form.referral_code || null },
      });
    } catch (error) {
      console.error("Verification/API error:", error);
      let msg = "Verification failed.";
      if (error.code === "auth/code-expired") {
        msg = "The verification code has expired. Please request a new one.";
        setStep(1);
      } else if (error.code === "auth/invalid-verification-code") {
        msg = "Invalid code. Please try again.";
      } else {
        msg = error.response?.data?.message || error.message;
      }
      setMessage({ text: msg, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light">
      <div className="card shadow-lg w-100" style={{ maxWidth: "420px" }}>
        <div className="card-body p-4">
          <h3 className="card-title text-center mb-3">
            {step === 1 ? "Create Your Account" : "Verify Your Phone"}
          </h3>
          <p className="text-muted text-center mb-4">
            {step === 1
              ? "Join us and start your journey."
              : `Enter the 6-digit code sent to ${form.countryCode}${form.phone}`}
          </p>

          {message.text && (
            <div
              className={`alert ${
                message.type === "success" ? "alert-success" : "alert-danger"
              }`}
            >
              {message.text}
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={handleSendVerification} noValidate>
              <InputField
                name="name"
                type="text"
                placeholder="Full Name"
                value={form.name}
                onChange={handleChange}
                error={errors.name}
              />
              <div className="input-group mb-3">
                <select
                  name="countryCode"
                  value={form.countryCode}
                  onChange={handleChange}
                  className="form-select"
                  style={{ maxWidth: "120px" }}
                >
                  {countryCodes.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="Phone Number"
                  className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                  required
                />
                {errors.phone && (
                  <div className="invalid-feedback w-100">{errors.phone}</div>
                )}
              </div>
              <InputField
                name="password"
                type="password"
                placeholder="Password"
                value={form.password}
                onChange={handleChange}
                error={errors.password}
              />
              <InputField
                name="password_confirmation"
                type="password"
                placeholder="Confirm Password"
                value={form.password_confirmation}
                onChange={handleChange}
                error={errors.password_confirmation}
              />
              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={isLoading}
              >
                {isLoading ? "Sending Code..." : "Register"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyCode} noValidate>
              <InputField
                name="verificationCode"
                type="text"
                placeholder="######"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="6"
              />
              <button
                type="submit"
                className="btn btn-success w-100 mb-2"
                disabled={isLoading}
              >
                {isLoading ? "Verifying..." : "Verify & Complete"}
              </button>
              <button
                type="button"
                className="btn btn-link w-100"
                onClick={() => setStep(1)}
              >
                Back to Registration
              </button>
            </form>
          )}

          <div id="recaptcha-container" className="mt-2"></div>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;
